import { Injectable } from '@angular/core';
import { SearchConfigService } from '../../search-results/services/search-config.service';
import { ClientStartupConfigService } from '../../shared/shared-context/client-startup-config.service';
import { Microsoft365Ring, SearchEnvironmentUrls, SearchInstrumentation, SearchInstrumenter, SearchPageBootstrapperService } from '../search-page-bootstrapper/search-page-bootstrapper.service';

@Injectable({
    providedIn: 'root'
})
export class SearchPageInstrumentationService {
    // SearchInstrumenter is used for performance marking and sharing of instrumentation
    // information across the SearchBox and the SERP
    public readonly searchUXInstrumenter: Promise<SearchInstrumenter> = new Promise(resolve => this.resolveSearchUXInstrumenter = resolve);
    // Used to resolve the Instrumentation module loaded Promise.
    private resolveSearchUXInstrumenter: (instrumenter: SearchInstrumenter) => void;

    // FAST Instrumentation module.
    public readonly searchUXInstrumentationModule: Promise<SearchInstrumentation> = new Promise(resolve => this.resolveSearchUXInstrumentationModule = resolve);
    // Used to resolve the Instrumentation module loaded Promise.
    private resolveSearchUXInstrumentationModule: (searchInstrumentation: SearchInstrumentation) => void;

    constructor(
        private readonly clientStartupConfigService: ClientStartupConfigService,
        private readonly searchConfigService: SearchConfigService,
        private readonly searchPageBootstrapperService: SearchPageBootstrapperService
    ) {
        this.initializeInstrumenter();
    }

    public async initializeInstrumentationLibrary(): Promise<void> {
        const instrumentation: SearchInstrumentation = await this.loadInstrumentation();

        this.resolveSearchUXInstrumentationModule(instrumentation);
    }

    public async initializeInstrumenter(): Promise<void> {
        await this.initializeInstrumentationLibrary();

        this.resolveSearchUXInstrumenter(
            (await this.searchUXInstrumentationModule).createInstrumenter({
                perf: window.performance,
                scenario: '',
                hostAppInfo: {
                    id: this.searchPageBootstrapperService.hostAppId,
                    version: this.clientStartupConfigService.config.sharedClientStartupContext.buildId,
                    userRing: this.getRing()
                },
                serviceEnvInfo: {
                    environment: this.searchPageBootstrapperService.getSearchEnvironment(),
                    urls: this.getSearchCloudUrls()
                }
            }
        ));
    }

    public getRing() {
        return this.searchConfigService.config.fastSearchUXEnvironment === 'prod' ?
            Microsoft365Ring.generalAudience :
            Microsoft365Ring.outer;
    }

    /**
     * True if we are in an airgap environment. Otherwise, false.
     *
     * @param farmSettings Farm settings from SPPageContext
     * @returns
     */
     private isAirGap(): boolean {
        const env: string = this.searchPageBootstrapperService.getSearchEnvironment();
        return env === 'ag08' || env === 'ag09';
    }

    /**
     * Gets search cloud urls (ECS, Aria, etc) for environments except Prod.
     *
     * @param farmSettings Farm settings from SPPageContext
     * @returns A list of urls used by search.
     */
    private getSearchCloudUrls(): SearchEnvironmentUrls | undefined {
        if (this.isAirGap() && this.searchConfigService.config.searchEnvironmentUrls) {
            return JSON.parse(this.searchConfigService.config.searchEnvironmentUrls) as SearchEnvironmentUrls;
        }

        return undefined;
    }

    private async loadInstrumentation(): Promise<SearchInstrumentation> {
        const searchPageModule = await this.searchPageBootstrapperService.searchPageModule;

        return await searchPageModule.loadSearchInstrumentation();
    }
}
